import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Sparkles, CheckCircle2, Circle, Loader2, GraduationCap, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api, daysUntil, urgencyColor } from "../lib/api";
import { AssignmentSpark } from "../components/AssignmentSpark";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";

const inputCls = "w-full bg-white border-2 border-black rounded-xl px-4 py-3 focus:outline-none focus:ring-4 focus:ring-[#FFE066]/50 text-base";

export default function CalendarPage() {
  const [assignments, setAssignments] = useState([]);
  const [open, setOpen] = useState(false);
  const [breaking, setBreaking] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ title: "", subject: "", type: "assignment", due_date: "", notes: "" });

  const load = useCallback(() => api.get("/assignments").then((r) => setAssignments(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.title || !form.subject || !form.due_date) {
      toast.error("Title, subject and due date are needed!");
      return;
    }
    await api.post("/assignments", form);
    toast.success("Added to the calendar!");
    setOpen(false);
    setForm({ title: "", subject: "", type: "assignment", due_date: "", notes: "" });
    load();
  };

  const remove = async (id) => {
    await api.delete(`/assignments/${id}`);
    load();
  };

  const complete = async (id) => {
    await api.patch(`/assignments/${id}/complete`);
    toast.success("Done and dusted! Huge win 🎉");
    load();
  };

  const breakdown = async (id) => {
    setBreaking(id);
    try {
      await api.post(`/assignments/${id}/breakdown`);
      toast.success("Mia chunked it into bite-size pieces!");
      load();
    } catch {
      toast.error("Couldn't break that down, try again.");
    }
    setBreaking(null);
  };

  const toggleChunk = async (aid, idx, done) => {
    await api.patch(`/assignments/${aid}/chunks/${idx}`, { done: !done });
    load();
  };

  const active = assignments.filter((a) => !a.completed);

  return (
    <div className="max-w-lg mx-auto px-5 pt-8 pb-32 space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-3xl font-semibold">Term Calendar</h1>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button
              data-testid="add-assignment-button"
              className="bg-[#4D96FF] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] hover:translate-y-[2px] hover:translate-x-[2px] hover:shadow-[2px_2px_0px_rgba(0,0,0,1)] transition-all font-bold p-3"
            >
              <Plus size={20} strokeWidth={3} />
            </button>
          </DialogTrigger>
          <DialogContent className="border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">Add assignment / exam</DialogTitle>
              <DialogDescription>Add a task and Mia will keep the pressure on early.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <input className={inputCls} placeholder="Title (e.g. English essay)" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="assignment-title-input" />
              <input className={inputCls} placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="assignment-subject-input" />
              <select className={inputCls} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="assignment-type-select">
                <option value="assignment">Assignment</option>
                <option value="exam">Exam</option>
                <option value="test">Test</option>
              </select>
              <input type="date" className={inputCls} value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="assignment-date-input" />
              <textarea className={inputCls} placeholder="Notes (optional)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="assignment-notes-input" />
              <button onClick={add} data-testid="assignment-save-button" className="w-full bg-[#FFE066] border-2 border-black rounded-full shadow-[4px_4px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-1 transition-all font-bold py-3">
                Add it
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {active.length === 0 && (
        <div className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-6 text-gray-600" data-testid="calendar-empty-msg">
          Calendar's empty! Add assignments with the + button or upload your assessment schedule.
        </div>
      )}

      {active.map((a) => {
        const d = daysUntil(a.due_date);
        const open = expandedId === a.id;
        return (
          <div key={a.id} className="bg-white border-2 border-black rounded-2xl shadow-[6px_6px_0px_rgba(0,0,0,1)] p-5 space-y-3" data-testid={`assignment-card-${a.id}`}>
            <button
              onClick={() => setExpandedId(open ? null : a.id)}
              data-testid={`expand-assignment-${a.id}`}
              className="w-full flex items-start justify-between gap-2 text-left"
            >
              <div className="min-w-0">
                <p className="font-bold text-lg leading-tight">{a.title}</p>
                <p className="text-sm text-gray-600 flex items-center gap-1"><GraduationCap size={14} /> {a.subject} · {a.type} · due {a.due_date.slice(0, 10)}</p>
              </div>
              <span className={`shrink-0 inline-flex items-center px-3 py-1 rounded-full border-2 border-black text-xs font-bold shadow-[2px_2px_0px_rgba(0,0,0,1)] ${urgencyColor(d)}`} data-testid={`urgency-badge-${a.id}`}>
                {d < 0 ? "overdue!" : d === 0 ? "today!" : `${d} day${d === 1 ? "" : "s"}`}
              </span>
              <ChevronDown size={18} className={`shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && <AssignmentSpark assignment={a} />}

            {a.chunks?.length > 0 && (
              <div className="space-y-2">
                {a.chunks.map((c, i) => (
                  <button
                    key={`${c.date}-${c.title}-${i}`}
                    onClick={() => toggleChunk(a.id, i, c.done)}
                    data-testid={`chunk-${a.id}-${i}`}
                    className={`w-full flex items-center gap-2 p-3 border-2 border-black rounded-xl text-left text-sm transition-all active:translate-y-[2px] ${
                      c.done ? "bg-[#51CF66] line-through" : "bg-[#FAFAFA] shadow-[2px_2px_0px_rgba(0,0,0,1)]"
                    }`}
                  >
                    {c.done ? <CheckCircle2 size={18} className="shrink-0" /> : <Circle size={18} className="shrink-0 text-gray-400" />}
                    <span className="flex-1 font-medium">{c.title}</span>
                    <span className="text-xs text-gray-500">{c.date.slice(5)}</span>
                  </button>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => breakdown(a.id)}
                disabled={breaking === a.id}
                data-testid={`breakdown-button-${a.id}`}
                className="flex-1 bg-[#F2C6DE] border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all font-bold py-2 text-sm flex items-center justify-center gap-1.5 disabled:opacity-60"
              >
                {breaking === a.id ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                {a.chunks?.length ? "Re-chunk" : "Break it down"}
              </button>
              <button onClick={() => complete(a.id)} data-testid={`complete-button-${a.id}`} className="bg-[#51CF66] border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all p-2.5">
                <CheckCircle2 size={18} />
              </button>
              <button onClick={() => remove(a.id)} data-testid={`delete-button-${a.id}`} className="bg-white border-2 border-black rounded-full shadow-[3px_3px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-y-[2px] transition-all p-2.5">
                <Trash2 size={18} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
